import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "./lib/AuthContext";
import { RealtimeProvider } from "./lib/RealtimeContext";
import { GamificationProvider } from "./lib/GamificationContext";
import { PaywallProvider, usePaywall } from "./lib/PaywallContext";
import { setPaywallHandler } from "./lib/api";
import ProtectedRoute from "./lib/ProtectedRoute";
import TierGuard from "./lib/TierGuard";
import Layout from "./components/Layout";
import SuspendedBanner from "./components/SuspendedBanner";
import { Toaster } from "./components/ui/sonner";
import PwaPrompts from "./components/PwaPrompts";
import PasskeyEnrollPrompt from "./components/PasskeyEnrollPrompt";

import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import BetaGate from "./components/BetaGate";
import ResetPassword from "./pages/ResetPassword";
import EmailSettings from "./pages/EmailSettings";
import Unsubscribe from "./pages/Unsubscribe";
import ForgotPassword from "./pages/ForgotPassword";
import Feed from "./pages/Feed";
import Explore from "./pages/Explore";
import AdventureDetail from "./pages/AdventureDetail";
import CreateAdventure from "./pages/CreateAdventure";
import Profile from "./pages/Profile";
import EditProfile from "./pages/EditProfile";
import Marketplace from "./pages/Marketplace";
import MarketplaceDetail from "./pages/MarketplaceDetail";
import CreateListing from "./pages/CreateListing";
import Pricing from "./pages/Pricing";
import Onboarding from "./pages/Onboarding";
import Settings from "./pages/Settings";
import PrivacySettings from "./pages/PrivacySettings";
import NotificationSettings from "./pages/NotificationSettings";
import Messages from "./pages/Messages";
import MessageThread from "./pages/MessageThread";
import Invites from "./pages/Invites";
import FollowRequests from "./pages/FollowRequests";
import Admin from "./pages/Admin";
import Brands from "./pages/Brands";
import BrandDetail from "./pages/BrandDetail";
import Passport from "./pages/Passport";
import Challenges from "./pages/Challenges";
import FriendChallenges from "./pages/FriendChallenges";
import FriendChallengeDetail from "./pages/FriendChallengeDetail";
import Leaderboards from "./pages/Leaderboards";
import OfflineGuides from "./pages/OfflineGuides";
import PackPage from "./pages/PackPage";
import Terms from "./pages/legal/Terms";
import Privacy from "./pages/legal/Privacy";
import CommunityGuidelines from "./pages/legal/CommunityGuidelines";

function Router() {
    return (
        <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<BetaGate pageTitle="Login"><Login /></BetaGate>} />
            <Route path="/signup" element={<BetaGate pageTitle="Sign up"><Signup /></BetaGate>} />
            <Route path="/register" element={<BetaGate pageTitle="Sign up"><Signup /></BetaGate>} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/unsubscribe" element={<Unsubscribe />} />
            <Route path="/pack/:id" element={<PackPage />} />

            <Route
                path="/settings/emails"
                element={<Navigate to="/settings?tab=emails" replace />}
            />
            <Route path="/forgot-password" element={<BetaGate pageTitle="Reset"><ForgotPassword /></BetaGate>} />
            <Route path="/pricing" element={<Pricing />} />

            {/* Public legal pages */}
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/community-guidelines" element={<CommunityGuidelines />} />

            <Route
                path="/onboarding"
                element={
                    <ProtectedRoute>
                        <Onboarding />
                    </ProtectedRoute>
                }
            />

            <Route
                path="/feed"
                element={
                    <ProtectedRoute>
                        <Layout><Feed /></Layout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/explore"
                element={
                    <Layout><Explore /></Layout>
                }
            />
            <Route
                path="/adventures/:id"
                element={
                    <Layout><AdventureDetail /></Layout>
                }
            />
            <Route
                path="/create"
                element={
                    <ProtectedRoute>
                        <TierGuard required="adventure" feature="post adventures">
                            <Layout><CreateAdventure /></Layout>
                        </TierGuard>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/adventures/:id/edit"
                element={
                    <ProtectedRoute>
                        <TierGuard required="adventure" feature="edit adventures">
                            <Layout><CreateAdventure /></Layout>
                        </TierGuard>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/profile"
                element={
                    <ProtectedRoute>
                        <Layout><Profile /></Layout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/profile/:userId"
                element={
                    <Layout><Profile /></Layout>
                }
            />
            <Route
                path="/profile/edit"
                element={<Navigate to="/settings?tab=profile" replace />}
            />
            <Route
                path="/marketplace"
                element={
                    <Layout><Marketplace /></Layout>
                }
            />
            <Route
                path="/marketplace/new"
                element={
                    <ProtectedRoute>
                        <TierGuard required="premium" feature="sell gear on the marketplace">
                            <Layout><CreateListing /></Layout>
                        </TierGuard>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/marketplace/:id"
                element={
                    <Layout><MarketplaceDetail /></Layout>
                }
            />
            <Route
                path="/settings"
                element={
                    <ProtectedRoute>
                        <Layout><Settings /></Layout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/settings/privacy"
                element={<Navigate to="/settings?tab=privacy" replace />}
            />
            <Route
                path="/settings/notifications"
                element={<Navigate to="/settings?tab=notifications" replace />}
            />
            <Route
                path="/messages"
                element={
                    <ProtectedRoute>
                        <Layout><Messages /></Layout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/messages/:threadId"
                element={
                    <ProtectedRoute>
                        <Layout><MessageThread /></Layout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/invites"
                element={
                    <ProtectedRoute>
                        <Layout><Invites /></Layout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/settings/follow-requests"
                element={
                    <ProtectedRoute>
                        <Layout><FollowRequests /></Layout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/admin"
                element={
                    <ProtectedRoute>
                        <Layout><Admin /></Layout>
                    </ProtectedRoute>
                }
            />
            <Route path="/brands" element={<Layout><Brands /></Layout>} />
            <Route path="/brands/:user_id" element={<Layout><BrandDetail /></Layout>} />
            <Route
                path="/passport"
                element={
                    <ProtectedRoute>
                        <TierGuard required="adventure" feature="view the Adventure Passport">
                            <Layout><Passport /></Layout>
                        </TierGuard>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/passport/:userId"
                element={
                    <TierGuard required="adventure" feature="view the Adventure Passport">
                        <Layout><Passport /></Layout>
                    </TierGuard>
                }
            />
            <Route
                path="/challenges"
                element={
                    <ProtectedRoute>
                        <TierGuard required="adventure" feature="take on challenges">
                            <Layout><Challenges /></Layout>
                        </TierGuard>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/friend-challenges"
                element={
                    <ProtectedRoute>
                        <TierGuard required="adventure" feature="join friend challenges">
                            <Layout><FriendChallenges /></Layout>
                        </TierGuard>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/friend-challenges/:cid"
                element={
                    <ProtectedRoute>
                        <TierGuard required="adventure" feature="join friend challenges">
                            <Layout><FriendChallengeDetail /></Layout>
                        </TierGuard>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/leaderboards"
                element={
                    <Layout><Leaderboards /></Layout>
                }
            />
            <Route
                path="/offline"
                element={
                    <ProtectedRoute>
                        <Layout><OfflineGuides /></Layout>
                    </ProtectedRoute>
                }
            />
        </Routes>
    );
}

function PaywallBridge() {
    // Subscribe the axios interceptor handler to our shared paywall modal.
    // Mounted once inside the provider tree so the handler always resolves
    // to the current `openPaywall` reference.
    const { openPaywall } = usePaywall();
    React.useEffect(() => {
        setPaywallHandler(openPaywall);
        return () => setPaywallHandler(null);
    }, [openPaywall]);
    return null;
}

function App() {
    return (
        <BrowserRouter>
            <AuthProvider>
                <RealtimeProvider>
                    <GamificationProvider>
                        <PaywallProvider>
                            <PaywallBridge />
                            <SuspendedBanner />
                            <Router />
                            <PwaPrompts />
                            <PasskeyEnrollPrompt />
                            <Toaster position="top-right" />
                        </PaywallProvider>
                    </GamificationProvider>
                </RealtimeProvider>
            </AuthProvider>
        </BrowserRouter>
    );
}

export default App;
